import { Filter, Mail, Search } from 'lucide-react'
import { useState } from 'react'
import type { EmailLog } from './types'

export default function EmailLogsTab({
  logs,
  pagination,
  loading,
  filters,
  setFilters,
}: {
  logs: EmailLog[]
  pagination?: any
  loading: boolean
  filters: any
  setFilters: (filters: any) => void
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateRange, setDateRange] = useState({ from: '', to: '' })

  const statusColors = {
    pending:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    sent: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    bounced:
      'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
    unknown: 'bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300',
    opened: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    clicked:
      'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
  }

  const statusLabels = {
    pending: '대기',
    sent: '발송완료',
    failed: '실패',
    bounced: '반송',
    unknown: '확인 불가',
    opened: '열람',
    clicked: '클릭',
  }

  const handleSearch = () => {
    setFilters({
      ...filters,
      search: searchTerm,
      status: statusFilter,
      dateFrom: dateRange.from,
      dateTo: dateRange.to,
    })
  }

  const handleReset = () => {
    setSearchTerm('')
    setStatusFilter('')
    setDateRange({ from: '', to: '' })
    setFilters({})
  }

  if (loading) {
    return (
      <div className="rounded-lg bg-white shadow dark:bg-gray-800">
        <div className="animate-pulse p-6">
          <div className="mb-4 h-4 w-1/4 rounded bg-gray-200 dark:bg-gray-700"></div>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="h-16 rounded bg-gray-200 dark:bg-gray-700"
              ></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg bg-white shadow dark:bg-gray-800">
      {/* 필터 및 검색 */}
      <div className="border-b border-gray-200 p-6 dark:border-gray-700">
        <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            메일 로그
          </h3>

          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="이메일, 제목, 캠페인명 검색..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">전체 상태</option>
                <option value="pending">대기</option>
                <option value="sent">발송완료</option>
                <option value="failed">실패</option>
                <option value="bounced">반송</option>
                <option value="opened">열람</option>
                <option value="clicked">클릭</option>
              </select>
            </div>

            <div className="flex gap-2">
              <input
                type="date"
                value={dateRange.from}
                onChange={e =>
                  setDateRange(prev => ({ ...prev, from: e.target.value }))
                }
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <input
                type="date"
                value={dateRange.to}
                onChange={e =>
                  setDateRange(prev => ({ ...prev, to: e.target.value }))
                }
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
              >
                <Search className="h-4 w-4" />
                검색
              </button>
              <button
                onClick={handleReset}
                className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <Filter className="h-4 w-4" />
                초기화
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 로그 목록 */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                수신자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                제목
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                상태
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                템플릿
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                발송일시
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                발송자
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
            {logs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center">
                  <Mail className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                  <p className="text-gray-500 dark:text-gray-400">
                    메일 로그가 없습니다.
                  </p>
                </td>
              </tr>
            ) : (
              logs.map(log => (
                <tr
                  key={log._id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {log.toEmail}
                    </div>
                    {log.userId && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {log.userId.name || log.userId.email}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs truncate text-sm text-gray-900 dark:text-white">
                      {log.subject}
                    </div>
                    {log.campaignName && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        캠페인: {log.campaignName}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                        statusColors[log.status as keyof typeof statusColors]
                      }`}
                    >
                      {statusLabels[log.status as keyof typeof statusLabels] ??
                        log.status}
                    </span>
                    {log.failureReason && (
                      <div className="mt-1 max-w-xs truncate text-xs text-red-600 dark:text-red-400">
                        {log.failureReason}
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {log.templateType || '-'}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    <div>{new Date(log.createdAt).toLocaleDateString()}</div>
                    <div>{new Date(log.createdAt).toLocaleTimeString()}</div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {log.senderAdminId
                      ? log.senderAdminId.name || log.senderAdminId.email
                      : '시스템'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      {pagination && pagination.totalPages > 1 && (
        <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              총 {(pagination?.totalLogs || 0).toLocaleString()}개 중{' '}
              {(pagination?.page - 1 || 0) * (pagination?.limit || 10) + 1}-
              {Math.min(
                (pagination?.page || 1) * (pagination?.limit || 10),
                pagination?.totalLogs || 0
              )}
              개 표시
            </div>
            <div className="flex gap-2">
              <button
                disabled={!pagination.hasPrevPage}
                className="rounded border border-gray-300 px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600"
              >
                이전
              </button>
              <span className="px-3 py-1 text-sm">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                disabled={!pagination.hasNextPage}
                className="rounded border border-gray-300 px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600"
              >
                다음
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// 이메일 템플릿 탭
